import { forwardRef, PropsWithoutRef, Suspense, useImperativeHandle, useState } from 'react';
import { experimentalValidateWithZod, FormTextareaField, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { AuthorizationType } from '@mews-commander/data-access';
import { Card, Stack } from '@optimus-web/core';
import { Loader } from '@mews-commander/ui';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { PaymentFormProps } from '../types';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import { NEW_CREDIT_CARD_VALUE } from '../../../../constants';
import {
    useGetAccountingConfigurationOptionsSuspense,
    useInvalidatePaymentMethodsTableRecordsQuery,
    useInvalidatePaymentsTableRecordsQueries,
    useInvalidatePreauthorizationTableRecordQuery,
    useRecordCard,
    useGetPaymentMethodsSuspense,
} from '../../../../queries';
import { isNewCard } from '../../../../utils';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { AuthorizationTypeSegmentControl } from '../AuthorizationTypeSegmentTabs/AuthorizationTypeSegmentControl';
import { PaymentMethodSelectField } from '../PaymentMethodSelectField/PaymentMethodSelectField';
import { useInvalidateGetPaymentPlansTableRecords } from '../../../tables/PaymentPlans/queries';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { RecordCardFormFields } from '../RecordCardForm/RecordCardFormFields';
import { useAddNonGatewayCardPayment, useGetChargeablePreauthorizationsSuspense } from '../queries';
import {
    createExternalCardPaymentForSelectedCardSchema,
    createExternalCardPaymentForNewCardSchema,
    createExternalCardPaymentForPreauthSchema,
    ExternalCardPaymentFormValues,
    ExternalCardPaymentFieldsSelectedCard,
    ExternalCardPaymentFieldsForNewCard,
    ExternalCardPaymentFieldsForPreauth,
    ExternalCardPaymentFormField,
} from './schema';

export const ExternalCardPaymentForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<PaymentFormProps>, ref) => {
        const invalidatePaymentsTableRecordsQueries = useInvalidatePaymentsTableRecordsQueries();
        const invalidatePreauthorizationRecords = useInvalidatePreauthorizationTableRecordQuery();
        const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();
        const invalidateGetPaymentPlansTableRecords = useInvalidateGetPaymentPlansTableRecords();
        const { data: accountingConfigurationOptions } = useGetAccountingConfigurationOptionsSuspense();

        const { defaultFormValues } = useDefaultFormContext();

        const {
            data: { paymentCards },
        } = useGetPaymentMethodsSuspense({ accountId });

        const { data: chargeablePreauthorizations } = useGetChargeablePreauthorizationsSuspense({
            accountId,
            isGateway: false,
        });
        const hasChargeablePreAuthorization = chargeablePreauthorizations?.length > 0;

        const recordCard = useRecordCard({ accountId });
        const addNonGatewayCardPayment = useAddNonGatewayCardPayment({ accountId });

        const [activeAuthorizationTypeSegment, setActiveAuthorizationTypeSegment] = useState<AuthorizationType>(
            hasChargeablePreAuthorization ? AuthorizationType.Preauthorization : AuthorizationType.CreditCardPayment
        );

        const { renderFormContent, submit, hasDirtyFields, watch, setValue } =
            useFormControls<ExternalCardPaymentFormValues>({
                onSubmit: async (values) => {
                    try {
                        onSubmitting?.();

                        let paymentCardId = hasPaymentMethodId(values) ? values.paymentMethodId : '';
                        let cardLastDigits = paymentCards?.find((p) => p.id === paymentCardId)?.lastDigits;
                        const preauthorizationId = hasPreAuthorizationId(values) ? values.preAuthorizationId : '';

                        if (areNewPaymentCardFormValues(values)) {
                            const { id, lastDigits } = await recordCard(values);

                            setValue(ExternalCardPaymentFormField.PaymentMethodId, id);
                            paymentCardId = id;
                            cardLastDigits = lastDigits;
                            invalidatePaymentMethods({ customerId: accountId });

                            showSuccessAlert({
                                description: lt('CardCreatedSuccessMessage', { cardLastDigits: lastDigits ?? '' }),
                            });
                        }

                        await addNonGatewayCardPayment(values, paymentCardId, preauthorizationId);

                        showSuccessAlert({
                            description: lt('CardPaymentSuccessMessage', {
                                cardLastDigits: cardLastDigits ?? '',
                                amount: localizeCurrencyValue({ value: values.amount, currency: values.currencyCode }),
                            }),
                        });

                        invalidatePreauthorizationRecords({ accountId });
                        invalidatePaymentsTableRecordsQueries({ accountId });

                        const { paymentPlanId } = values;
                        invalidateGetPaymentPlansTableRecords(paymentPlanId);

                        onSuccess?.();
                    } finally {
                        onSubmitted?.();
                    }
                },
                defaultValues: {
                    ...defaultFormValues,
                },
                resolver: (values) => {
                    let schema;
                    if (hasPreAuthorizationId(values)) {
                        const preauthorizationAmount = chargeablePreauthorizations.find(
                            ({ id }) => id === values.preAuthorizationId
                        )?.amount.value;
                        schema = createExternalCardPaymentForPreauthSchema(preauthorizationAmount ?? 0);
                    } else if (
                        hasPaymentMethodId(values) &&
                        isNewCard(values[ExternalCardPaymentFormField.PaymentMethodId])
                    ) {
                        schema = createExternalCardPaymentForNewCardSchema(
                            accountingConfigurationOptions.optionalCreditCardPaymentDetails
                        );
                    } else {
                        schema = createExternalCardPaymentForSelectedCardSchema(
                            accountingConfigurationOptions.optionalCreditCardPaymentDetails
                        );
                    }
                    return experimentalValidateWithZod(schema)(values);
                },
            });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );
        return renderFormContent(
            <Stack spacing="300" vertical>
                <PaymentCommonSection />
                {hasChargeablePreAuthorization ? (
                    <AuthorizationTypeSegmentControl
                        activeAuthorizationTypeSegment={activeAuthorizationTypeSegment}
                        setActiveAuthorizationTypeSegment={setActiveAuthorizationTypeSegment}
                        accountId={accountId}
                        isExternal
                        cardPaymentItemLabel={lt('ExternalCardPayment')}
                    />
                ) : (
                    <PaymentMethodSelectField
                        name={ExternalCardPaymentFormField.PaymentMethodId}
                        label={lt('PaymentCard')}
                        helper={lt('ProcessPaymentMewsPaymentsCardPaymentCardSelectionHint')}
                        accountId={accountId}
                        optionalCreditCardPaymentDetails={
                            accountingConfigurationOptions.optionalCreditCardPaymentDetails
                        }
                        required
                    />
                )}

                {activeAuthorizationTypeSegment === AuthorizationType.CreditCardPayment &&
                isNewCard(watch(ExternalCardPaymentFormField.PaymentMethodId)) ? (
                    <Card padding="300" outline>
                        <Suspense fallback={<Loader />}>
                            <RecordCardFormFields />
                        </Suspense>
                    </Card>
                ) : null}
                <PaymentAmountSection
                    isCurrencyFieldDisabled={activeAuthorizationTypeSegment === AuthorizationType.Preauthorization}
                />
                <FormTextareaField
                    name={ExternalCardPaymentFormField.ReceiptIdentifier}
                    label={lt('ReceiptIdentifier')}
                    helper={lt('ProcessPaymentExternalPaymentsCardPaymentReceiptIdentifierHint')}
                    rows={1}
                    required={
                        !accountingConfigurationOptions.optionalCreditCardPaymentDetails &&
                        activeAuthorizationTypeSegment === AuthorizationType.CreditCardPayment
                    }
                />
                <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={1} />
            </Stack>
        );
    }
);

const areNewPaymentCardFormValues = (
    values: ExternalCardPaymentFormValues
): values is ExternalCardPaymentFieldsForNewCard =>
    'paymentMethodId' in values && values.paymentMethodId === NEW_CREDIT_CARD_VALUE;

function hasPaymentMethodId(
    values: ExternalCardPaymentFormValues
): values is ExternalCardPaymentFieldsForNewCard | ExternalCardPaymentFieldsSelectedCard {
    return 'paymentMethodId' in values;
}

function hasPreAuthorizationId(values: ExternalCardPaymentFormValues): values is ExternalCardPaymentFieldsForPreauth {
    return 'preAuthorizationId' in values;
}
